import DeleteConfirmation from 'components/delete-confirmation/DeleteConfirmation';
import { MenuTitles, Pages } from 'constants/menu';
import { AgencyContext } from 'contexts';
import { ContentLayout, PageLayout } from 'layout';
import { AgxToast, enumToOptions } from '@urbanx/agx-ui-components';
import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { StaffAction } from 'types/staff';
import { AgxToastState, NotificationState } from 'types/commonTypes';
import { isEqual } from 'lodash-es';

import { FormPrompt } from '../../components/FormPrompt';
import RoleAssignmentModal from './RoleAssignmentModel';
import StaffMemberFormPage, {
  type StaffMemberFormHandle,
  type StaffMemberFormValues,
  type StaffMemberSubmissionResult,
} from './StaffMemberFormPage';
import { useDoesUserHasRequiredRoles } from 'helpers/doesUserHaveRequiredRoles';
import { AuthContext, StaffRole } from '@urbanx/agx.auth.client';
import {
  StaffModel,
  UploadedDocument,
  useDeleteStaffMember,
  useGetAllStaff,
  useInvalidateUseGetAllStaff,
  useInvalidateUseGetStaffByRoles,
  useInvalidateUseGetStaff,
  useSendInvitationEmail,
  useUpdateStaffMember,
} from '@urbanx/agx.staff.client';
import { getEnumValueByValue } from 'helpers/enumHelper';
import {
  AgenciesApiContext,
  UrbanXTeamByAgenciesResponse,
  useAssignStaffMemberToAgencies,
  useGetAgenciesAssignedToStaffMember,
  useGetAllAgencies,
  useInvalidateUseGetTeamsForAgency,
  useInvalidateUseGetUrbanXTeamForAgency,
  useInvalidateUseGetStaffMembersAssignedToAgencies,
  useRemovePublicDocument,
  useUploadStaffPhoto,
} from '@urbanx/agx.agencies.client';
import {
  isValidEmail,
  AgxButton,
  AgxEmptyState,
  AgxSpacer,
  AgxSaveChangesButton,
  Icon_Arrow,
  SAVE_BUTTON_STATES,
} from '@urbanx/agx.ui-components2';
import {
  dedupeRoleConflicts,
  getAvailableStaffRoleOptions,
  mapAgenciesToOptions,
  rolesAssignableToAgency,
  staffStateOptions,
} from './staffPageUtils';

const staffRoleOptions = enumToOptions(StaffRole);
const emptyStaffMember: StaffModel = {};

const EditStaffMember = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams<{ userId?: string }>();
  const staffRouteKey = userId?.trim();
  const [selectedAgency] = useContext(AgencyContext);
  const { userAccount } = useContext(AuthContext);
  const loggedInStaffId = userAccount?.agent || '';
  const onBack = () => navigate(Pages.STAFF);

  const { data: allStaffMembers, isPending: allStaffMembersPending } = useGetAllStaff();
  const invalidateGetAllStaff = useInvalidateUseGetAllStaff();
  const invalidateStaffByRoles = useInvalidateUseGetStaffByRoles();
  const invalidateGetStaff = useInvalidateUseGetStaff();
  const invalidateTeamsForAgency = useInvalidateUseGetTeamsForAgency();
  const invalidateUrbanXTeamForAgency = useInvalidateUseGetUrbanXTeamForAgency();
  const createdStaffFromRouteState = location.state?.createdStaff as
    | StaffMemberFormValues
    | undefined;

  const staffMember = staffRouteKey
    ? allStaffMembers?.find((staff) => staff.id === staffRouteKey)
    : undefined;

  const routeStateMatchesCurrentUserId =
    !!staffRouteKey &&
    !!createdStaffFromRouteState &&
    createdStaffFromRouteState.id === staffRouteKey;

  const selectedStaffMember =
    staffMember ??
    (routeStateMatchesCurrentUserId ? createdStaffFromRouteState : undefined) ??
    emptyStaffMember;

  const isMissingStaffMember =
    !userId || (!!userId && !allStaffMembersPending && !selectedStaffMember.id);

  useEffect(() => {
    if (isMissingStaffMember) {
      navigate(Pages.STAFF, { replace: true });
    }
  }, [isMissingStaffMember, navigate]);

  const formRef = useRef<StaffMemberFormHandle>(null);
  const [staffMemberForm, setStaffMemberForm] = useState<StaffMemberFormValues>({
    ...selectedStaffMember,
    roles: selectedStaffMember.roles,
  });
  const [img, setImg] = useState<string | undefined>(selectedStaffMember.photo?.publicPath);
  const [isBoundFormDirty, setIsBoundFormDirty] = useState(false);
  const [agencyBaseline, setAgencyBaseline] = useState<string[]>([]);
  const [hasPhotoChanges, setHasPhotoChanges] = useState(false);
  const [forceCheck, setForceCheck] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [pendingRoleAssignmentCheck, setPendingRoleAssignmentCheck] = useState(false);
  const [roleConflicts, setRoleConflicts] = useState<UrbanXTeamByAgenciesResponse[]>([]);
  const [roleConflictRoles, setRoleConflictRoles] = useState<string[]>([]);
  const [saveButtonState, setSaveButtonState] = useState<SAVE_BUTTON_STATES>(
    SAVE_BUTTON_STATES.UNSAVED
  );
  const [isFormInitialised, setIsFormInitialised] = useState(false);
  const [toastState, setToastState] = useState<AgxToastState>({
    color: 'success',
    message: '',
    open: false,
  });
  const [emailInviteState, setEmailInviteState] = useState<NotificationState>(
    selectedStaffMember.emailInvitationSent ? NotificationState.Sent : NotificationState.NotSent
  );

  const [agencyIds, setAgencyIds] = useState<string[]>([]);
  const submissionResolver = useRef<((result: StaffMemberSubmissionResult) => void) | undefined>(
    undefined
  );
  const pendingSubmission = useRef<
    { staffMember: StaffMemberFormValues; agencyIds: string[] } | undefined
  >(undefined);

  useEffect(() => {
    setIsFormInitialised(false);
    setStaffMemberForm({
      ...selectedStaffMember,
      roles: selectedStaffMember.roles,
    });
    setImg(selectedStaffMember.photo?.publicPath);
    setEmailInviteState(
      selectedStaffMember.emailInvitationSent ? NotificationState.Sent : NotificationState.NotSent
    );
    setIsBoundFormDirty(false);
    setAgencyBaseline([]);
    setHasPhotoChanges(false);
    setSaveButtonState(SAVE_BUTTON_STATES.UNSAVED);
    setAgencyIds([]);
    setRoleConflicts([]);
    setRoleConflictRoles([]);
    setPendingRoleAssignmentCheck(false);
    setForceCheck(false);
    setShowWarningModal(false);
    setIsFormInitialised(true);
  }, [selectedStaffMember.id, userId]);

  const doesUserHaveRequiredRoles = useDoesUserHasRequiredRoles();

  const availableStaffRoleOptions = getAvailableStaffRoleOptions(
    staffRoleOptions,
    doesUserHaveRequiredRoles
  );

  const selectedRoles = staffMemberForm.roles;

  const roleIsAssignableToAgency =
    selectedRoles?.some((role) => rolesAssignableToAgency.includes(role)) ?? false;

  const hasAgencyChanges = !isEqual(agencyIds, agencyBaseline);
  const isFormDirty = isBoundFormDirty || hasAgencyChanges || hasPhotoChanges;

  const resolveSubmission = (result: StaffMemberSubmissionResult) => {
    submissionResolver.current?.(result);
    submissionResolver.current = undefined;
    pendingSubmission.current = undefined;
  };

  const { data: agencies, isPending: agenciesPending } = useGetAllAgencies();

  const { getAgenciesAssignedToStaffMember } = useGetAgenciesAssignedToStaffMember({
    onSuccess: () => undefined,
    onError: () => undefined,
  });

  useEffect(() => {
    if (!roleIsAssignableToAgency || !selectedStaffMember.id) {
      setAgencyIds((prev) => (prev.length === 0 ? prev : []));
      setAgencyBaseline([]);
      return;
    }

    let active = true;
    void getAgenciesAssignedToStaffMember({
      staffMemberId: selectedStaffMember.id,
      roles: selectedRoles,
    })
      .then((returnData) => {
        if (!active) return;
        const nextAgencyIds = returnData ?? [];
        setAgencyIds((prev) => (isEqual(prev, nextAgencyIds) ? prev : nextAgencyIds));
        setAgencyBaseline(nextAgencyIds);
      })
      .catch((error: Error) => {
        if (!active) return;
        console.error('Error loading staff member agencies:', error);
        setAgencyIds([]);
        setAgencyBaseline([]);
      });

    return () => {
      active = false;
    };
  }, [
    getAgenciesAssignedToStaffMember,
    roleIsAssignableToAgency,
    selectedStaffMember.id,
    selectedRoles,
  ]);

  const invalidateGetStaffMembersAssignedToAgencies =
    useInvalidateUseGetStaffMembersAssignedToAgencies();
  const agenciesApi = useContext(AgenciesApiContext);

  const invalidateManageUrbanXTeamQueries = async () => {
    await Promise.all([
      invalidateTeamsForAgency(),
      invalidateUrbanXTeamForAgency(),
      invalidateStaffByRoles(),
    ]);
  };

  const { updateStaffMember, updateStaffMemberPending } = useUpdateStaffMember({
    onSuccess: async (_, updatedStaffMember) => {
      const submittedAgencyIds = pendingSubmission.current?.agencyIds ?? [];
      await Promise.all([
        invalidateGetAllStaff(),
        ...(updatedStaffMember.id ? [invalidateGetStaff(updatedStaffMember.id)] : []),
        invalidateManageUrbanXTeamQueries(),
      ]);
      setAgencyBaseline(submittedAgencyIds);
      setHasPhotoChanges(false);
      setSaveButtonState(SAVE_BUTTON_STATES.SAVED);
      resolveSubmission({ status: 'accepted' });
      setToastState({
        color: 'success',
        message: 'Changes saved successfully',
        open: true,
      });
    },
    onError: (error) => {
      resolveSubmission({ status: 'failed', error });
      setSaveButtonState(SAVE_BUTTON_STATES.ERROR);
      setToastState({
        color: 'error',
        message: 'Error saving changes',
        open: true,
      });
    },
  });

  const { deleteStaffMember } = useDeleteStaffMember({
    onSuccess: async () => {
      await Promise.all([invalidateGetAllStaff(), invalidateManageUrbanXTeamQueries()]);
      onBack();
    },
    onError: () => {
      setToastState({
        color: 'error',
        message: 'Error saving changes',
        open: true,
      });
    },
  });

  const { assignStaffMemberToAgencies, assignStaffMemberToAgenciesPending } =
    useAssignStaffMemberToAgencies({
      onSuccess: async () => {
        const submission = pendingSubmission.current;
        const submittedRoles = submission?.staffMember.roles ?? [];
        const submittedAgencyIds = submission?.agencyIds ?? [];
        const submittedAssignableRoles = submittedRoles.filter((role) =>
          rolesAssignableToAgency.includes(role)
        );

        await Promise.all([
          ...submittedAssignableRoles.map((role) =>
            invalidateGetStaffMembersAssignedToAgencies(role, submittedAgencyIds)
          ),
          invalidateManageUrbanXTeamQueries(),
        ]);
      },
      onError: (error) => {
        resolveSubmission({ status: 'failed', error });
        setSaveButtonState(SAVE_BUTTON_STATES.ERROR);
        setToastState({
          color: 'error',
          message: 'Error occurred assigning staff to agencies',
          open: true,
        });
      },
    });

  const confirmStaffMemberUpdate = async () => {
    setShowWarningModal(false);

    const submission = pendingSubmission.current;
    if (!submission) return;
    const roles = submission.staffMember.roles ?? [];

    if (!selectedStaffMember.id) {
      setSaveButtonState(SAVE_BUTTON_STATES.ERROR);
      resolveSubmission({ status: 'failed', error: new Error('Missing staff member id') });
      return;
    }

    if (roles.length === 0) {
      setSaveButtonState(SAVE_BUTTON_STATES.ERROR);
      resolveSubmission({ status: 'failed', error: new Error('Missing staff roles') });
      return;
    }

    const staffRoles: StaffRole[] = roles
      .map((role) => getEnumValueByValue(StaffRole, role))
      .filter((role): role is StaffRole => role !== undefined);

    if (staffRoles.length === 0) {
      setSaveButtonState(SAVE_BUTTON_STATES.ERROR);
      resolveSubmission({ status: 'failed', error: new Error('Missing staff roles') });
      return;
    }

    try {
      await assignStaffMemberToAgencies({
        staffMemberId: selectedStaffMember.id,
        staffRoles,
        agencyIds: roles.some((role) => rolesAssignableToAgency.includes(role))
          ? submission.agencyIds
          : [],
      });
      await updateStaffMember(submission.staffMember);
    } catch (error) {
      resolveSubmission({ status: 'failed', error });
      setSaveButtonState(SAVE_BUTTON_STATES.ERROR);
      setToastState({
        color: 'error',
        message: 'Failed to update staff member',
        open: true,
      });
    }
  };

  const checkCurrentRoleAssignments = async () => {
    const submission = pendingSubmission.current;
    if (!submission) return;

    const submittedAssignableRoles = (submission.staffMember.roles ?? []).filter((role) =>
      rolesAssignableToAgency.includes(role)
    );
    const shouldCheckAssignments =
      submittedAssignableRoles.length > 0 && submission.agencyIds.length > 0;

    if (shouldCheckAssignments) {
      try {
        const conflictResponses = await Promise.all(
          submittedAssignableRoles.map(async (role) => {
            const response = await agenciesApi.getStaffMembersAssignedToAgencies(
              role,
              submission.agencyIds
            );

            return {
              staffRole: response.staffRole ?? role,
              staffRoleAssignments: response.staffRoleAssignments ?? [],
            } as UrbanXTeamByAgenciesResponse;
          })
        );

        const dedupedRoleConflicts = dedupeRoleConflicts(conflictResponses, selectedStaffMember.id);

        if (dedupedRoleConflicts.length > 0) {
          setRoleConflicts(dedupedRoleConflicts);
          setRoleConflictRoles(submittedAssignableRoles);
          setPendingRoleAssignmentCheck(false);
          setShowWarningModal(true);
          return;
        }
      } catch (error) {
        setPendingRoleAssignmentCheck(false);
        resolveSubmission({ status: 'failed', error });
        setSaveButtonState(SAVE_BUTTON_STATES.ERROR);
        setToastState({
          color: 'error',
          message: 'Error validating role assignments',
          open: true,
        });
        return;
      }
    }

    setRoleConflicts([]);
    setPendingRoleAssignmentCheck(false);
    void confirmStaffMemberUpdate();
  };

  const onValidSubmit = (values: StaffMemberFormValues) => {
    setStaffMemberForm(values);
    pendingSubmission.current = { staffMember: values, agencyIds: [...agencyIds] };
    setSaveButtonState(SAVE_BUTTON_STATES.SAVING);
    setPendingRoleAssignmentCheck(true);
    return new Promise<StaffMemberSubmissionResult>((resolve) => {
      submissionResolver.current = resolve;
    });
  };

  useEffect(() => {
    if (!pendingRoleAssignmentCheck) {
      return;
    }

    void checkCurrentRoleAssignments();
  }, [pendingRoleAssignmentCheck, agenciesApi, selectedStaffMember.id]);

  const { sendInvitationEmail } = useSendInvitationEmail({
    onSuccess: async () => {
      setEmailInviteState(NotificationState.Sent);
      await invalidateGetAllStaff();
      setToastState({
        color: 'success',
        message: 'Email Sent!',
        open: true,
      });
    },
    onError: () => {
      setEmailInviteState(NotificationState.NotSent);
      setToastState({
        color: 'error',
        message: 'Error sending email invitation',
        open: true,
      });
    },
  });

  const onSendInvite = () => {
    if (emailInviteState !== NotificationState.Sending) {
      setEmailInviteState(NotificationState.Sending);
      sendInvitationEmail({ staffMemberId: selectedStaffMember.id });
    }
  };

  const { uploadStaffPhoto, uploadStaffPhotoPending: isUploading } = useUploadStaffPhoto({
    onSuccess: (value) => {
      setImg(value?.publicPath || '');
      setStaffMemberForm((current) => ({ ...current, photo: value as UploadedDocument }));
      setHasPhotoChanges(true);
    },
    onError: () => {
      setToastState({
        color: 'error',
        message: 'Error uploading photo',
        open: true,
      });
    },
  });

  const onUploadPhoto = (file: File) => {
    setImg(URL.createObjectURL(file));
    return uploadStaffPhoto({ file });
  };

  const { removePublicDocument: removePhoto, removePublicDocumentPending: isRemovingPhoto } =
    useRemovePublicDocument({
      onSuccess: () => {
        setImg(undefined);
        setStaffMemberForm((current) => ({ ...current, photo: undefined }));
        setHasPhotoChanges(true);
      },
      onError: () => {
        setToastState({
          color: 'error',
          message: 'Error removing photo',
          open: true,
        });
      },
    });

  const isSavingChanges =
    updateStaffMemberPending ||
    assignStaffMemberToAgenciesPending ||
    pendingRoleAssignmentCheck ||
    showWarningModal;
  const isPhotoActionPending = isUploading || isRemovingPhoto;

  const saveChangesState = isSavingChanges
    ? SAVE_BUTTON_STATES.SAVING
    : saveButtonState === SAVE_BUTTON_STATES.ERROR
      ? SAVE_BUTTON_STATES.ERROR
      : isFormDirty
        ? SAVE_BUTTON_STATES.UNSAVED
        : saveButtonState;

  const agenciesOptions = mapAgenciesToOptions(agencies, agenciesPending);

  const onBackClick = () => {
    if (isFormDirty) {
      setForceCheck(true);
      return;
    }

    onBack();
  };

  if (isMissingStaffMember) return null;

  const isLoading = allStaffMembersPending || agenciesPending || !isFormInitialised;

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={selectedAgency?.name || ''}
      currentPageTitle={MenuTitles.TEAM}
      isStaff
    >
      <ContentLayout hasSecondaryMenu secondaryActiveMenu={MenuTitles.TEAM} isStaff>
        {isLoading ? (
          <AgxEmptyState variant="minimal" label="Loading" />
        ) : (
          <AgxSpacer direction="vertical" gap="spacing-5xl" fill>
            <FormPrompt
              hasUnsavedChanges={isFormDirty}
              forceCheck={forceCheck}
              resetForceCheck={(value: boolean) => setForceCheck(value)}
              onBack={onBack}
            />

            <AgxToast selector="#agxToast" toastState={toastState} />

            <div className="borderBottomContainer">
              <AgxButton
                id="return-button"
                variant="naked"
                text="Edit Staff Member"
                leftIcon={<Icon_Arrow direction="left" height={22} width={22} />}
                onClick={onBackClick}
              />

              <AgxSpacer
                direction="horizontal"
                className="growOne"
                contentJustification="end"
                contentAlignment="center"
                gap="spacing-5xl"
              >
                {loggedInStaffId !== selectedStaffMember.id && (
                  <AgxButton
                    id="btn-delete-user"
                    text="Delete User"
                    variant="naked"
                    size="medium"
                    danger
                    onClick={() => setShowDeleteConfirmation(true)}
                  />
                )}

                <AgxSaveChangesButton
                  id="btnSaveChanges"
                  text="Save Changes"
                  variant="primary"
                  size="medium"
                  state={saveChangesState}
                  onClick={() => void formRef.current?.submit()}
                  disabled={isPhotoActionPending || isSavingChanges || !isFormDirty}
                />
              </AgxSpacer>
            </div>

            <StaffMemberFormPage
              ref={formRef}
              pageAction={StaffAction.EDIT}
              staffMember={staffMemberForm}
              photoUri={img}
              isSaving={isSavingChanges}
              isPhotoActionPending={isPhotoActionPending}
              onValidSubmit={onValidSubmit}
              availableStaffRoleOptions={availableStaffRoleOptions}
              stateOptions={staffStateOptions}
              showAgencies={roleIsAssignableToAgency}
              agencyIds={agencyIds}
              agenciesOptions={agenciesOptions}
              showEmailInvitation
              emailInviteState={emailInviteState}
              emailInviteDisabled={
                emailInviteState === NotificationState.Sending ||
                !isValidEmail(staffMemberForm.email) ||
                isSavingChanges
              }
              onDirtyChange={setIsBoundFormDirty}
              onRolesChange={(roles) => {
                setStaffMemberForm((current) => ({ ...current, roles }));
                setSaveButtonState(SAVE_BUTTON_STATES.UNSAVED);
              }}
              onAgencyIdsChange={(value: string[]) => {
                if (!isEqual(agencyIds, value)) {
                  setAgencyIds(value);
                  setSaveButtonState(SAVE_BUTTON_STATES.UNSAVED);
                }
              }}
              onUploadPhoto={onUploadPhoto}
              onRemovePhoto={() => {
                removePhoto({
                  filePath: staffMemberForm.photo?.containerFilePath || '',
                });
              }}
              onSendInvite={onSendInvite}
            />

            <DeleteConfirmation
              name={`${selectedStaffMember.firstName} ${selectedStaffMember.lastName}`}
              title="Really delete user?"
              lineOne="Are you sure you want to delete user"
              lineTwo="This cannot be undone."
              primaryActionTitle="Permanently delete user"
              secondaryActionTitle="No, cancel"
              showPopUp={showDeleteConfirmation}
              onClose={() => setShowDeleteConfirmation(false)}
              onConfirm={() => deleteStaffMember(staffMemberForm)}
            />

            {showWarningModal && (
              <RoleAssignmentModal
                setShowModal={setShowWarningModal}
                updateRoleAssignments={confirmStaffMemberUpdate}
                onCancel={() =>
                  resolveSubmission({
                    status: 'failed',
                    error: new Error('Role assignment was cancelled'),
                  })
                }
                selectedRoles={roleConflictRoles}
                roleConflicts={roleConflicts}
              />
            )}
          </AgxSpacer>
        )}
      </ContentLayout>
    </PageLayout>
  );
};

export default EditStaffMember;
